import React from 'react'
import { Col, Row } from 'antd'
import cn from 'classnames'
import { useGeneralContext } from 'contexts/GeneralContext'
import { currentLanguageCodeVar, currentMarketIdVar } from 'graphql/reactive'
import { useTranslation } from 'lib/i18n'
import { trackPageview } from 'lib/tracking'
import { NextSeo } from 'next-seo'

import CloseButton from 'components/CloseButton/CloseButton'
import DefaultHeadTags from 'components/DefaultHeadTags/DefaultHeadTags'
import Heading from 'components/Heading/Heading'
import Wrapper from 'components/Wrapper/Wrapper'
import useEffectOnce from 'hooks/useEffectOnce'
import usePath from 'hooks/usePath'

import MarketOption from './MarketOption'

import styles from './MarketSelectorUi.module.css'

const MarketSelectorUi = ({ markets, handleMarketChange }) => {
  const { t } = useTranslation()
  const path = usePath()
  const marketId = currentMarketIdVar()
  const languageCode = currentLanguageCodeVar()
  const url = path.asPath

  const { setCloseToPath } = useGeneralContext()

  const selectedMarket = markets?.find((market) => market.id === marketId)

  const selectableMarkets = markets
    ?.filter(({ showInMarketPicker }) => showInMarketPicker)
    .sort((a, b) => a.name.localeCompare(b.name))

  // If we have a selectable global market, move it to the front of the list
  if (selectableMarkets.some((market) => market.id === 'global')) {
    selectableMarkets.unshift(
      selectableMarkets.splice(
        selectableMarkets.findIndex((market) => {
          return market.id === 'global'
        }),
        1,
      )[0],
    )
  }

  useEffectOnce(() => {
    if (url === '/') {
      window?.analytics?.page(url)
      trackPageview({ path: url })
    }
    setCloseToPath(`${marketId}/${languageCode}`)
  })
  return (
    <>
      <DefaultHeadTags />
      <NextSeo
        title={t(
          'app.meta.title1',
          'Personalized video messages from celebrities - memmo.me',
        )}
      />
      <section className={styles.base}>
        <Wrapper className={styles.wrapper}>
          <div className={styles.close}>
            <CloseButton color="#000" />
          </div>

          {selectedMarket && (
            <Row className={styles.wrapperRow}>
              <Col xs={24} md={{ span: 12, pull: 6, push: 6 }}>
                <div>
                  <Heading color="gray" level="h3">
                    {t(
                      'MarketSelectorUi.suggested',
                      'Suggested region and language.',
                    )}
                  </Heading>
                </div>

                <MarketOption
                  market={selectedMarket}
                  handleMarketChange={handleMarketChange}
                  selected
                  featuredMarket={selectedMarket.id === 'global'}
                />
              </Col>
            </Row>
          )}

          <Row className={styles.wrapperRow}>
            <Col xs={24} md={{ span: 12, pull: 6, push: 6 }}>
              <div className={styles.header}>
                <Heading level="h3" color="gray">
                  <h1>
                    {t('MarketSelectorUi.title', 'Select your memmo market')}
                  </h1>
                </Heading>
              </div>

              <div
                data-cy="marketGrid"
                className={cn('test_marketOptions', styles.marketGrid)}
              >
                {selectableMarkets?.map((market) => (
                  <MarketOption
                    key={market.id}
                    market={market}
                    handleMarketChange={handleMarketChange}
                    featuredMarket={market.id === 'global'}
                  />
                ))}
              </div>
            </Col>
          </Row>
        </Wrapper>
      </section>
    </>
  )
}

export default MarketSelectorUi
